import clsx from 'clsx';
import { type MouseEvent, useState } from 'react';
import {
  GraphIcon,
  type GraphIconShapeState,
  VARIANT_CLASS,
} from '../graph-icon/GraphIcon';
import type { NetworkGraphNodeModel } from './NetworkGraph';
import styles from './NetworkGraph.module.scss';
import type { NetworkGraphPosition } from './utils/computeRadialLayout';
import {
  nodeDiameter,
  nodeLabelBox,
  nodeOuterRadius,
  SELECTED_LABEL_PADDING,
} from './utils/nodeGeometry';
import type { LabelOffset } from './utils/resolveLabelCollisions';

export interface NetworkGraphNodeProps {
  node: NetworkGraphNodeModel;
  position: NetworkGraphPosition;
  connectedPositions?: NetworkGraphPosition[];
  // Layout placement ray (degrees, anchor→node); see nodeLabelBox.
  rayAngle?: number;
  labelOffset?: LabelOffset;
  onClick?: () => void;
  onHover?: (isHovered: boolean) => void;
  isHovered?: boolean;
}

export const NetworkGraphNode = ({
  node,
  position,
  connectedPositions = [],
  rayAngle,
  labelOffset,
  onClick,
  onHover,
  isHovered = false,
}: NetworkGraphNodeProps) => {
  const [isPointerOver, setIsPointerOver] = useState(false);
  const isActiveSize = node.size !== 'default';
  const diameter = nodeDiameter(node.size);
  const outerRadius = nodeOuterRadius(node.size);
  const fallbackContent = Array.from(node.label.trim())[0]?.toUpperCase() ?? '';
  const isInteractive = node.isInteractive !== false && Boolean(onClick);
  const labelBox = nodeLabelBox({
    node,
    position,
    connectedPositions,
    rayAngle,
  });
  const labelX = labelBox.x - position.x + (labelOffset?.x ?? 0);
  const labelY = labelBox.y - position.y + (labelOffset?.y ?? 0);
  // Pin the text's node-facing edge to the rect edge (radius+gap) rather than
  // centering it, so the estimated-vs-real width error lands on the far edge and
  // the node→label gap stays consistent between nodes.
  const labelTextX =
    ({ start: -1, middle: 0, end: 1 }[labelBox.anchor] * labelBox.textWidth) /
    2;
  const isHoverable = node.isInteractive !== false;
  const shapeState: GraphIconShapeState = isActiveSize
    ? 'active'
    : isHoverable && (isPointerOver || isHovered)
      ? 'hover'
      : 'default';

  const handleClick = (event: MouseEvent<SVGGElement>) => {
    if (!isInteractive || !onClick) {
      return;
    }
    event.stopPropagation();
    onClick();
  };

  const handleHover = (hovered: boolean) => {
    if (isHoverable) {
      setIsPointerOver(hovered);
    }
    onHover?.(hovered);
  };

  return (
    <g
      className={clsx(styles.node, VARIANT_CLASS[node.variant])}
      data-interactive={isInteractive || undefined}
      data-network-graph-node-id={node.id}
      data-size={node.size}
      data-state={node.state}
      data-testid="network-graph-node"
      onClick={handleClick}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
      // role="button" opts the node out of the SVG pan-capture in
      // useFlowChartPanZoom so its click is not swallowed by drag-to-pan.
      role={isInteractive ? 'button' : undefined}
      transform={`translate(${position.x} ${position.y})`}
    >
      <g className={styles.nodeVisual}>
        {isActiveSize ? (
          <foreignObject
            aria-hidden="true"
            className={styles.activeContentObject}
            height={outerRadius * 2}
            width={outerRadius * 2}
            x={-outerRadius}
            y={-outerRadius}
          >
            <GraphIcon
              data={{
                kind: 'shape',
                variant: node.variant,
                logo: node.activeContent ?? fallbackContent,
                state: shapeState,
              }}
              size={diameter}
            />
          </foreignObject>
        ) : (
          <GraphIcon
            data={{
              kind: 'shape',
              variant: node.variant,
              state: shapeState,
            }}
            size={diameter}
          />
        )}
        {node.isLoading ? (
          <circle
            className={styles.loadingRing}
            r={outerRadius + 6}
            data-network-graph-loading
          />
        ) : null}
      </g>
      <g
        className={styles.nodeLabelGroup}
        data-visible={
          node.labelVisible || isPointerOver || isHovered || undefined
        }
        // Rotated along the node's edge (same normalization as the edge
        // labels) so the name continues its arrow's ray instead of laying a
        // horizontal box across the neighboring rays.
        transform={`translate(${labelX} ${labelY}) rotate(${labelBox.angle})`}
      >
        <rect
          className={styles.nodeLabelBackground}
          height={labelBox.textHeight}
          rx={SELECTED_LABEL_PADDING}
          width={labelBox.textWidth}
          x={-labelBox.textWidth / 2}
          y={-labelBox.textHeight / 2}
        />
        <text
          className={styles.nodeLabel}
          style={{ textAnchor: labelBox.anchor }}
          x={labelTextX}
        >
          {node.label}
        </text>
      </g>
    </g>
  );
};
